import type { AutomationDetail, Step } from "./types";

/** Badge variant for an automation or run status. */
export function statusVariant(status: string): "success" | "warning" | "destructive" | "muted" | "default" {
  switch (status) {
    case "active":
    case "completed":
    case "goal_reached":
    case "ok":
      return "success";
    case "shadow":
    case "waiting":
    case "paused":
    case "skipped":
      return "warning";
    case "failed":
      return "destructive";
    case "archived":
    case "exited":
    case "draft":
      return "muted";
  }
  return "default";
}

/** Plain words for statuses. */
export function statusLabel(status: string): string {
  const m: Record<string, string> = {
    shadow: "shadow (logs only)",
    goal_reached: "reached a goal",
    active: "running",
  };
  return m[status] ?? status.replace(/_/g, " ");
}

export interface StepRow {
  number: number;
  key: string;
  type: string;
  label: string;
  people: number;
  shadowPeople: number;
  then: string; // "then: Wait 1d" / branches
  note?: string;
  startsHere: string[]; // trigger labels that start at this step
}

function nextKey(steps: Step[], i: number): string {
  const s = steps[i];
  if (s.next) return s.next;
  return i + 1 < steps.length ? steps[i + 1].key : "end";
}

/** The step list for the diagram: each step with its label, how many people are there now, and where it goes. */
export function stepRows(d: AutomationDetail): StepRow[] {
  const def = d.definition;
  if (!def) return [];
  const labels = new Map<string, string>();
  for (const n of d.graph?.nodes ?? []) if (n.kind === "step" && n.key) labels.set(n.key, n.label);
  const labelOf = (k: string) => (k === "end" ? "End" : labels.get(k) ?? k);
  const numberOf = new Map(def.steps.map((s, i) => [s.key, i + 1]));
  const ref = (k: string) => (k === "end" ? "End" : `#${numberOf.get(k) ?? "?"} ${labelOf(k)}`);
  const people = new Map<string, number>();
  const shadow = new Map<string, number>();
  for (const c of d.step_counts) {
    if (c.version_id !== d.automation.current_version_id) continue;
    const m = c.shadow ? shadow : people;
    m.set(c.step_key, (m.get(c.step_key) ?? 0) + c.people);
  }
  const starts = new Map<string, string[]>();
  def.triggers.forEach((t, i) => {
    const at = t.start_at || def.steps[0]?.key;
    if (!at) return;
    const name = t.name || (d.graph?.nodes.find((n) => n.id === "trigger:" + (t.key || "t" + (i + 1)))?.label ?? t.type);
    starts.set(at, [...(starts.get(at) ?? []), name]);
  });
  return def.steps.map((s, i) => {
    let then: string;
    if (s.type === "branch") {
      const parts = (s.branches ?? []).map((b) => `${b.name || "if"} → ${ref(b.next)}`);
      const otherwise = s.else || (s.next ? s.next : nextKey(def.steps, i));
      parts.push(`otherwise → ${ref(otherwise)}`);
      then = parts.join(" · ");
    } else if (s.type === "exit") {
      then = "End";
    } else {
      then = ref(nextKey(def.steps, i));
      if (s.type === "goal" && s.timeout) then += ` · after ${s.timeout} → ${ref(s.timeout_next || "end")}`;
    }
    return {
      number: i + 1,
      key: s.key,
      type: s.type,
      label: labelOf(s.key),
      people: people.get(s.key) ?? 0,
      shadowPeople: shadow.get(s.key) ?? 0,
      then,
      note: s.note,
      startsHere: starts.get(s.key) ?? [],
    };
  });
}

/** Short words for step types. */
export const STEP_TYPES: Record<string, string> = {
  email: "Email",
  wait: "Wait",
  time_window: "Time window",
  wait_until: "Wait until",
  tag_add: "Add tag",
  tag_remove: "Remove tag",
  field_set: "Set field",
  webhook: "Webhook",
  note: "Note",
  staff_alert: "Team alert",
  sign_in_link: "Sign-in link",
  facebook_audience: "Facebook audience",
  branch: "Decision",
  goal: "Goal",
  automation_start: "Start automation",
  automation_stop: "Stop automation",
  exit: "Exit",
};
